import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BadgeCheck, CreditCard, Loader2, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const NON_RACING_ROLES = ["Spectator", "Official", "Supporter"];
const RACING_CLASSES = ["Formula 500s", "Wingless Sprints", "Street Stocks", "Super Rods", "Junior Quarter Midgets / JQMV", "Other"];

const TIERS = [
  { key: "season_membership", label: "Adult Single", price: 35, note: "Ages 18+" },
  { key: "junior_membership", label: "Junior Single", price: 15, note: "12 – 18 years" },
  { key: "family_membership", label: "Family", price: 60, note: "2 adults + 2 children" },
  { key: "kids_free", label: "Kids Under 12", price: 0, note: "Free" },
];

const PERKS = [
  "Full 2026/27 season membership",
  "Supports venue upgrades & running costs",
  "Digital membership number issued instantly",
  "Voting rights at Mortlake Car Club meetings",
];

export default function Membership({ open, setOpen }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [tier, setTier] = useState("season_membership");
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address_street: "",
    address_suburb: "",
    address_postcode: "",
    racing_class: "Spectator",
    registering_car: false,
    car_details: "",
    insurance: "",
    wwcc: false,
    wwcc_card: "",
    wwcc_expiry: "",
  });
  const activeTier = TIERS.find((t) => t.key === tier);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const fullName = `${form.first_name.trim()} ${form.last_name.trim()}`.trim();
  const fullAddress = [form.address_street.trim(), form.address_suburb.trim(), form.address_postcode.trim()].filter(Boolean).join(", ");
  const valid =
    form.first_name.trim() &&
    form.last_name.trim() &&
    /.+@.+\..+/.test(form.email) &&
    form.phone.trim() &&
    form.address_street.trim() &&
    form.address_suburb.trim() &&
    form.address_postcode.trim() &&
    form.insurance !== "" &&
    (!form.registering_car || form.racing_class) &&
    (!form.wwcc || form.wwcc_expiry);

  const close = () => {
    setOpen(false);
    setTimeout(() => setStep(1), 300);
  };

  const pay = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: tier,
        origin_url: window.location.origin,
        ...form,
        name: fullName,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const registerFree = async () => {
    setLoading(true);
    try {
      await api.post("/membership/free-kids", {
        name: fullName,
        email: form.email,
        phone: form.phone,
        address_street: form.address_street,
        address_suburb: form.address_suburb,
        address_postcode: form.address_postcode,
        origin_url: window.location.origin,
      });
      setStep(3);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 text-clay-text placeholder:text-clay-muted/60 transition-colors duration-300 font-sans";

  return (
    <>
      <section id="membership" className="max-w-[1400px] mx-auto px-4 sm:px-8 py-20 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-clay-orange mb-4">Join the Family</p>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-5xl lg:text-6xl leading-[0.95] mb-6">
              Keep the Lights <br />
              <span className="text-clay-orange">Burning.</span>
            </h2>
            <p className="text-clay-muted text-lg leading-relaxed max-w-xl mb-8">
              Become a valued member for only $35 per season. Your support goes straight towards constant venue
              improvements and running costs — ensuring our club's continuation into the future.
            </p>
            <ul className="space-y-3">
              {PERKS.map((p) => (
                <li key={p} className="flex items-center gap-3 text-clay-text/90">
                  <BadgeCheck className="w-5 h-5 text-clay-gold shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-5 border border-clay-border bg-clay-card p-8 sm:p-10 relative overflow-hidden"
          >
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-clay-orange/10 rotate-45" />
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-clay-muted mb-2">Season 2026/27</p>
            <div className="flex items-end gap-2 mb-6">
              <span className="font-display font-black text-7xl text-clay-text leading-none">$35</span>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-clay-muted pb-2">AUD / season</span>
            </div>
            <button
              data-testid="membership-open-button"
              onClick={() => setOpen(true)}
              className="w-full bg-clay-orange hover:bg-clay-gold text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-8 py-4 transition-all duration-300 hover:-translate-y-1 clay-glow mb-4"
            >
              Become a Member
            </button>
            <p className="flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-clay-muted">
              <ShieldCheck className="w-4 h-4 text-clay-gold" /> Secure online payment
            </p>
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="membership-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={close}
          >
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 80, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-clay-surface border border-clay-border p-6 sm:p-10 max-h-[92vh] overflow-y-auto"
            >
              <div className="flex items-start justify-between mb-8">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay-orange mb-2">
                    {step === 3 ? "All Done" : `Step ${step} of 2`}
                  </p>
                  <h3 className="font-display font-bold uppercase text-3xl tracking-tight">
                    {step === 1 ? "Member Details" : step === 2 ? "Confirm & Pay" : "You're In"}
                  </h3>
                </div>
                <button data-testid="membership-close-button" onClick={close} className="text-clay-muted hover:text-clay-text transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>

              {step === 1 ? (
                <div className="space-y-4">
                  <div data-testid="membership-tier-select" className="grid grid-cols-2 gap-2">
                    {TIERS.map((t) => (
                      <button
                        key={t.key}
                        data-testid={`tier-${t.key}`}
                        type="button"
                        onClick={() => setTier(t.key)}
                        className={`text-left border p-3.5 transition-all duration-300 ${
                          tier === t.key
                            ? "border-clay-orange bg-clay-orange/10 clay-glow"
                            : "border-clay-border bg-clay-bg hover:border-clay-orange/50"
                        }`}
                      >
                        <span className="block font-display font-bold uppercase tracking-wide text-sm">{t.label}</span>
                        <span className={`block font-mono text-xs mt-1 ${tier === t.key ? "text-clay-gold" : "text-clay-muted"}`}>
                          {t.price === 0 ? "FREE" : `$${t.price} AUD`}
                        </span>
                        <span className="block font-mono text-[10px] text-clay-muted/70 mt-0.5">{t.note}</span>
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <input data-testid="membership-form-first-name-input" placeholder="First name *" value={form.first_name} onChange={set("first_name")} className={inputCls} />
                    <input data-testid="membership-form-last-name-input" placeholder="Last name *" value={form.last_name} onChange={set("last_name")} className={inputCls} />
                  </div>
                  <input data-testid="membership-form-email-input" type="email" placeholder="Email address *" value={form.email} onChange={set("email")} className={inputCls} />
                  <input data-testid="membership-form-phone-input" placeholder="Phone *" value={form.phone} onChange={set("phone")} className={inputCls} />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay-muted mb-2">Mailing address *</p>
                    <div className="space-y-4">
                      <input data-testid="membership-address-street-input" placeholder="Street address *" value={form.address_street} onChange={set("address_street")} className={inputCls} />
                      <div className="grid grid-cols-2 gap-4">
                        <input data-testid="membership-address-suburb-input" placeholder="Suburb / Town *" value={form.address_suburb} onChange={set("address_suburb")} className={inputCls} />
                        <input data-testid="membership-address-postcode-input" placeholder="Postcode *" inputMode="numeric" value={form.address_postcode} onChange={set("address_postcode")} className={inputCls} />
                      </div>
                    </div>
                  </div>

                  <div className="border border-clay-border bg-clay-bg p-4 space-y-4">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay-muted mb-2">Are you registering a car? *</p>
                      <div className="flex gap-2">
                        {[true, false].map((v) => (
                          <button
                            key={String(v)}
                            type="button"
                            data-testid={`membership-car-${v ? "yes" : "no"}-button`}
                            onClick={() =>
                              setForm((f) => ({
                                ...f,
                                registering_car: v,
                                racing_class: v ? "" : NON_RACING_ROLES.includes(f.racing_class) ? f.racing_class : "Spectator",
                              }))
                            }
                            className={`flex-1 font-display font-bold uppercase tracking-wider py-2.5 border transition-colors duration-300 ${
                              form.registering_car === v
                                ? "bg-clay-orange text-clay-bg border-clay-orange"
                                : "border-clay-border text-clay-muted hover:border-clay-orange/60"
                            }`}
                          >
                            {v ? "Yes" : "No"}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay-muted mb-2">
                        {form.registering_car ? "Racing class *" : "I'm joining as a…"}
                      </p>
                      <select data-testid="membership-form-class-select" value={form.racing_class} onChange={set("racing_class")} className={inputCls}>
                        {form.registering_car && <option value="" disabled>Select your racing class…</option>}
                        {(form.registering_car ? RACING_CLASSES : NON_RACING_ROLES).map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    {form.registering_car && (
                      <input
                        data-testid="membership-car-details-input"
                        placeholder="Car number and class — e.g. V42 Street Stock"
                        value={form.car_details}
                        onChange={set("car_details")}
                        className={inputCls}
                      />
                    )}
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay-muted mb-2">
                        Speedway Australia or AASA insurance? *
                      </p>
                      <select data-testid="membership-insurance-select" value={form.insurance} onChange={set("insurance")} className={inputCls}>
                        <option value="" disabled>Select…</option>
                        <option value="Speedway Australia">Yes — Speedway Australia</option>
                        <option value="AASA">Yes — AASA</option>
                        <option value="Both — Speedway Australia &amp; AASA">Yes — Both Speedway Australia &amp; AASA</option>
                        <option value="None">No / Not yet</option>
                      </select>
                    </div>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay-muted mb-2">
                        Current Working With Children Check? *
                      </p>
                      <div className="flex gap-2">
                        {[true, false].map((v) => (
                          <button
                            key={String(v)}
                            type="button"
                            data-testid={`membership-wwcc-${v ? "yes" : "no"}-button`}
                            onClick={() => setForm((f) => ({ ...f, wwcc: v }))}
                            className={`flex-1 font-display font-bold uppercase tracking-wider py-2.5 border transition-colors duration-300 ${
                              form.wwcc === v
                                ? "bg-clay-orange text-clay-bg border-clay-orange"
                                : "border-clay-border text-clay-muted hover:border-clay-orange/60"
                            }`}
                          >
                            {v ? "Yes" : "No"}
                          </button>
                        ))}
                      </div>
                    </div>
                    {form.wwcc && (
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          data-testid="membership-wwcc-card-input"
                          placeholder="WWCC card number"
                          value={form.wwcc_card}
                          onChange={set("wwcc_card")}
                          className={inputCls}
                        />
                        <div>
                          <input
                            data-testid="membership-wwcc-expiry-input"
                            type="date"
                            value={form.wwcc_expiry}
                            onChange={set("wwcc_expiry")}
                            className={`${inputCls} ${form.wwcc_expiry ? "" : "text-clay-muted/60"}`}
                          />
                          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-clay-muted mt-1">WWCC expiry date *</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    data-testid="membership-form-submit-button"
                    disabled={!valid}
                    onClick={() => setStep(2)}
                    className="w-full bg-clay-orange hover:bg-clay-gold disabled:opacity-40 disabled:cursor-not-allowed text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-8 py-4 transition-all duration-300 mt-2"
                  >
                    Continue
                  </button>
                </div>
              ) : step === 2 ? (
                <div className="space-y-6">
                  <div className="border border-clay-border bg-clay-bg p-5 space-y-2 font-mono text-sm">
                    <div className="flex justify-between"><span className="text-clay-muted">Name</span><span>{fullName}</span></div>
                    <div className="flex justify-between"><span className="text-clay-muted">Email</span><span className="truncate ml-4">{form.email}</span></div>
                    <div className="flex justify-between"><span className="text-clay-muted shrink-0">Address</span><span className="text-right ml-4">{fullAddress}</span></div>
                    <div className="flex justify-between"><span className="text-clay-muted">Class</span><span>{form.racing_class}</span></div>
                    <div className="flex justify-between"><span className="text-clay-muted">Car</span><span>{form.registering_car ? form.car_details || "Yes" : "No"}</span></div>
                    <div className="flex justify-between"><span className="text-clay-muted">Insurance</span><span>{form.insurance}</span></div>
                    <div className="flex justify-between"><span className="text-clay-muted">WWCC</span><span>{form.wwcc ? `Yes${form.wwcc_card ? ` — ${form.wwcc_card}` : ""}${form.wwcc_expiry ? ` (expires ${form.wwcc_expiry})` : ""}` : "No"}</span></div>
                    <div className="flex justify-between"><span className="text-clay-muted">Tier</span><span>{activeTier.label}</span></div>
                    <div className="flex justify-between border-t border-clay-border pt-2 mt-2">
                      <span className="text-clay-muted">Membership 26/27</span>
                      <span className="text-clay-gold font-bold">
                        {activeTier.price === 0 ? "FREE" : `$${activeTier.price}.00 AUD`}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      data-testid="membership-back-button"
                      onClick={() => setStep(1)}
                      className="flex-1 border border-clay-border hover:border-clay-orange font-display font-bold uppercase tracking-wider px-6 py-4 transition-colors duration-300"
                    >
                      Back
                    </button>
                    {activeTier.price === 0 ? (
                      <button
                        data-testid="free-register-button"
                        onClick={registerFree}
                        disabled={loading}
                        className="flex-[2] bg-clay-orange hover:bg-clay-gold disabled:opacity-60 text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-6 py-4 transition-all duration-300 flex items-center justify-center gap-2 clay-glow"
                      >
                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <BadgeCheck className="w-5 h-5" />}
                        {loading ? "Registering…" : "Register — Free"}
                      </button>
                    ) : (
                      <button
                        data-testid="stripe-checkout-button"
                        onClick={pay}
                        disabled={loading}
                        className="flex-[2] bg-clay-orange hover:bg-clay-gold disabled:opacity-60 text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-6 py-4 transition-all duration-300 flex items-center justify-center gap-2 clay-glow"
                      >
                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                        {loading ? "Redirecting…" : `Pay $${activeTier.price} Securely`}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center space-y-5 py-4">
                  <BadgeCheck className="w-14 h-14 text-clay-gold mx-auto" />
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay-orange">Registered — Free Membership</p>
                  <h4 className="font-display font-bold uppercase text-2xl tracking-tight">Welcome aboard!</h4>
                  <p className="text-clay-muted text-sm">A confirmation email is on its way to {form.email}.</p>
                  <button
                    data-testid="free-done-button"
                    onClick={close}
                    className="border border-clay-border hover:border-clay-orange font-display font-bold uppercase tracking-wider px-8 py-3 transition-colors duration-300"
                  >
                    Done
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
